import { useEffect, useLayoutEffect, useSyncExternalStore } from "react";

import { useActiveBusiness } from "../auth/useActiveBusiness";
import { useAuth } from "../auth/AuthContext";
import { loadAllBranding } from "./brandingApi";
import { getBranding, subscribeBranding, brandingVersion } from "./brandingStore";
import { cssVarsFor } from "./palette";

/* ------------------------------------------------------------------ *
 * Branding hooks + the :root theme.
 *
 * ThemeRoot writes the open business's palette to <html> as --rf-*
 * custom properties. Modals render through portals and print pages are
 * bare routes, so :root is the one place all of them inherit from.
 * With no business open (login, group overview) the defaults in
 * theme.css apply.
 * ------------------------------------------------------------------ */

/** Re-render when any business's branding changes. */
function useBrandingVersion() {
  return useSyncExternalStore(subscribeBranding, brandingVersion, brandingVersion);
}

/** Resolved branding for `business` ({ id|business_id, name, code }). */
export function useBranding(business) {
  useBrandingVersion();
  return getBranding(business);
}

/** Branding for the business that is open right now. */
export function useActiveBranding() {
  return useBranding(useActiveBusiness());
}

/** The open business's palette tokens — for code that needs real hex
    values (Chart.js draws on a canvas and can't read var()). */
export function useThemeTokens() {
  return useActiveBranding().tokens;
}

let applied = [];

export function ThemeRoot() {
  const active = useActiveBusiness();
  const branding = useBranding(active);

  // Every business's saved branding, once per signed-in person — the
  // overview and the business switcher show other businesses' themes too.
  const { user } = useAuth() || {};
  const userId = user ? user.id || user.userName : null;
  useEffect(() => {
    if (userId) loadAllBranding();
  }, [userId]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    applied.forEach((name) => root.style.removeProperty(name));
    applied = [];
    if (!active) return;
    const vars = cssVarsFor(branding.tokens);
    const logo = branding.logo || {};
    if (logo.height) vars["--rf-logo-h"] = `${logo.height}px`;
    if (logo.print_height) vars["--rf-logo-print-h"] = `${logo.print_height}px`;
    Object.keys(vars).forEach((name) => {
      root.style.setProperty(name, vars[name]);
      applied.push(name);
    });
  }, [active, branding]);

  return null;
}
